import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { supplierInvoiceAPI } from "@/lib/api";
import {
  Plus, Search, Trash2, Edit, Receipt, Loader2, X, Wallet, Check
} from "lucide-react";
import SupplierInvoiceDialog from "@/components/SupplierInvoiceDialog";
import { DateField } from "@/components/ui/date-field";

const MONTHS = [
  { value: 1, label: "Janv" }, { value: 2, label: "Fév" }, { value: 3, label: "Mars" },
  { value: 4, label: "Avr" }, { value: 5, label: "Mai" }, { value: 6, label: "Juin" },
  { value: 7, label: "Juil" }, { value: 8, label: "Août" }, { value: 9, label: "Sept" },
  { value: 10, label: "Oct" }, { value: 11, label: "Nov" }, { value: 12, label: "Déc" },
];

const PAYMENT_LABELS = {
  cb: "CB", virement: "Virement", especes: "Espèces",
  cheque: "Chèque", prélèvement: "Prélèv.", autre: "Autre",
  compte_courant_associe: "💳 Perso", "": "-",
};

const formatEur = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);
const formatDate = (d) => {
  if (!d) return "-";
  try { return new Date(d).toLocaleDateString("fr-FR"); } catch { return d; }
};

function getYearOptions() {
  const current = new Date().getFullYear();
  const years = [];
  for (let y = current; y >= current - 5; y--) years.push(y);
  return years;
}

function getDateRange(year, month, fullYear) {
  if (fullYear) {
    return { start: `${year}-01-01`, end: `${year}-12-31` };
  }
  if (month) {
    const m = String(month).padStart(2, "0");
    const lastDay = new Date(year, month, 0).getDate();
    return { start: `${year}-${m}-01`, end: `${year}-${m}-${lastDay}` };
  }
  return { start: `${year}-01-01`, end: `${year}-12-31` };
}

export default function SupplierInvoicesPage() {
  const [searchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [types, setTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [persoDialog, setPersoDialog] = useState(null);
  const [persoForm, setPersoForm] = useState({ paid_at: "", note: "" });
  const [persoSaving, setPersoSaving] = useState(false);

  useEffect(() => {
    if (persoDialog) setPersoForm({ paid_at: persoDialog.date || new Date().toISOString().slice(0, 10), note: "" });
  }, [persoDialog]);

  const handleMarkPaidPersonally = async () => {
    if (!persoDialog) return;
    setPersoSaving(true);
    try {
      await supplierInvoiceAPI.markPaidPersonally(persoDialog.id, persoForm);
      toast.success(`Facture ${persoDialog.supplier_name} marquée comme réglée en perso`);
      setPersoDialog(null);
      loadInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setPersoSaving(false); }
  };

  const handleUnmarkPaidPersonally = async (inv) => {
    try {
      await supplierInvoiceAPI.unmarkPaidPersonally(inv.id);
      toast.success(`Règlement perso annulé pour ${inv.supplier_name}`);
      loadInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [fullYear, setFullYear] = useState(false);
  const [useCustomDates, setUseCustomDates] = useState(false);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  // Auto-open dialog if ?new=1
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setDialogOpen(true);
    }
  }, [searchParams]);

  // Ref for filter state to keep loadInvoices stable
  const filtersRef = useRef({});
  filtersRef.current = { search, typeFilter, selectedYear, selectedMonth, fullYear, useCustomDates, customStart, customEnd };

  const loadInvoices = useCallback(async () => {
    const f = filtersRef.current;
    setLoading(true);
    try {
      const params = {};
      if (f.search) params.search = f.search;
      if (f.typeFilter) params.type = f.typeFilter;

      if (f.useCustomDates) {
        if (f.customStart) params.date_start = f.customStart;
        if (f.customEnd) params.date_end = f.customEnd;
      } else {
        const range = getDateRange(f.selectedYear, f.selectedMonth, f.fullYear);
        params.date_start = range.start;
        params.date_end = range.end;
      }

      const res = await supplierInvoiceAPI.list(params);
      setInvoices(res.data.items || []);
      setTotal(res.data.total || 0);
      if (res.data.types) setTypes(res.data.types);
    } catch {
      toast.error("Erreur chargement des factures fournisseurs");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadInvoices(); }, [search, typeFilter, selectedYear, selectedMonth, fullYear, useCustomDates, customStart, customEnd, loadInvoices]);

  // Auto-repair orphan supplier links on first load
  useEffect(() => {
    supplierInvoiceAPI.repairSuppliers().catch(() => {});
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette facture fournisseur ?")) return;
    try {
      await supplierInvoiceAPI.delete(id);
      toast.success("Facture supprimée");
      loadInvoices();
    } catch { toast.error("Erreur suppression"); }
  };

  const handleCreated = () => {
    setDialogOpen(false);
    setEditInvoice(null);
    loadInvoices();
  };

  const totalHT = invoices.reduce((s, i) => s + (i.total_ht || 0), 0);
  const totalTVA = invoices.reduce((s, i) => s + (i.tva_amount || 0), 0);
  const totalTTC = invoices.reduce((s, i) => s + (i.total_ttc || 0), 0);

  // Period label
  let periodLabel = "";
  if (useCustomDates) {
    periodLabel = "Dates personnalisees";
  } else if (fullYear) {
    periodLabel = `Année ${selectedYear}`;
  } else {
    periodLabel = `${MONTHS[selectedMonth - 1]?.label || ""} ${selectedYear}`;
  }

  return (
    <div className="p-4 lg:p-8 space-y-5" data-testid="supplier-invoices-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="supplier-invoices-title">
          Factures fournisseurs
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">{total} facture{total > 1 ? "s" : ""} — {periodLabel}</p>
      </div>

      {/* Date filter bar + add button */}
      <Card className="p-3 bg-white border border-slate-200" data-testid="period-filter">
        <div className="flex flex-wrap items-center gap-3">
          {/* Year selector */}
          <div className="flex items-center gap-1.5">
            {getYearOptions().map(y => (
              <button
                key={y}
                onClick={() => { setSelectedYear(y); setUseCustomDates(false); }}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${selectedYear === y && !useCustomDates ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                data-testid={`year-${y}`}
              >
                {y}
              </button>
            ))}
          </div>

          {/* Full year checkbox */}
          <label className="flex items-center gap-1.5 cursor-pointer select-none border-l border-slate-200 pl-3" data-testid="full-year-toggle">
            <input
              type="checkbox"
              checked={fullYear}
              onChange={(e) => { setFullYear(e.target.checked); setUseCustomDates(false); }}
              className="rounded border-slate-300 h-3.5 w-3.5"
            />
            <span className="text-xs text-slate-600">Toute l'année</span>
          </label>

          {/* Custom dates toggle */}
          <button
            onClick={() => setUseCustomDates(!useCustomDates)}
            className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors border-l border-slate-200 ml-0 pl-3 ${useCustomDates ? "text-indigo-600" : "text-slate-500 hover:text-slate-700"}`}
            data-testid="custom-dates-toggle"
          >
            Dates
          </button>

          {/* Add button - right aligned */}
          <div className="ml-auto">
            <Button
              size="sm"
              onClick={() => { setEditInvoice(null); setDialogOpen(true); }}
              className="bg-indigo-600 hover:bg-indigo-700 h-8"
              data-testid="add-supplier-invoice-btn"
            >
              <Plus size={14} className="mr-1" />
              Nouvelle facture
            </Button>
          </div>
        </div>

        {/* Month pills (hidden if full year or custom dates) */}
        {!fullYear && !useCustomDates && (
          <div className="flex flex-wrap gap-1 mt-2.5 pt-2.5 border-t border-slate-100" data-testid="month-selector">
            {MONTHS.map(m => (
              <button
                key={m.value}
                onClick={() => { setSelectedMonth(m.value); setUseCustomDates(false); }}
                className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${selectedMonth === m.value ? "bg-indigo-100 text-indigo-700 border border-indigo-200" : "text-slate-500 hover:bg-slate-100 border border-transparent"}`}
                data-testid={`month-${m.value}`}
              >
                {m.label}
              </button>
            ))}
          </div>
        )}

        {/* Custom date inputs */}
        {useCustomDates && (
          <div className="flex gap-2 mt-2.5 pt-2.5 border-t border-slate-100 items-center" data-testid="custom-dates-row">
            <span className="text-xs text-slate-500">Du</span>
            <DateField value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="h-8 text-xs w-36" data-testid="date-start" />
            <span className="text-xs text-slate-500">au</span>
            <DateField value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="h-8 text-xs w-36" data-testid="date-end" />
          </div>
        )}
      </Card>

      {/* Search + type filter */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[180px] max-w-xs">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Rechercher..."
              className="pl-8 h-9 text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="search-supplier-invoices"
            />
          </div>
        </div>
        <select
          className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm min-w-[160px]"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          data-testid="type-filter-select"
        >
          <option value="">Tous les types</option>
          {Object.entries(types).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        {(search || typeFilter) && (
          <Button variant="ghost" size="sm" onClick={() => { setSearch(""); setTypeFilter(""); }} data-testid="clear-filters-btn">
            <X size={14} className="mr-1" /> Effacer
          </Button>
        )}
      </div>

      {/* Summary cards with TVA emphasis */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4 bg-white border border-slate-200" data-testid="summary-total-ht">
          <p className="text-xs text-slate-500 font-medium">Total HT</p>
          <p className="text-xl font-bold text-slate-900 mt-1">{formatEur(totalHT)}</p>
        </Card>
        <Card className="p-4 bg-emerald-50 border border-emerald-200" data-testid="summary-total-tva">
          <p className="text-xs text-emerald-600 font-semibold">TVA deductible</p>
          <p className="text-2xl font-bold text-emerald-700 mt-1">{formatEur(totalTVA)}</p>
          <p className="text-[11px] text-emerald-500 mt-0.5">{periodLabel}</p>
        </Card>
        <Card className="p-4 bg-white border border-slate-200" data-testid="summary-total-ttc">
          <p className="text-xs text-slate-500 font-medium">Total TTC</p>
          <p className="text-xl font-bold text-slate-900 mt-1">{formatEur(totalTTC)}</p>
        </Card>
      </div>

      {/* Table */}
      <Card className="bg-white border border-slate-200 overflow-hidden" data-testid="supplier-invoices-table">
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 size={24} className="animate-spin mx-auto text-slate-400" />
            <p className="text-sm text-slate-500 mt-2">Chargement...</p>
          </div>
        ) : invoices.length === 0 ? (
          <div className="p-12 text-center" data-testid="empty-state">
            <Receipt size={40} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-500">Aucune facture fournisseur sur cette période</p>
            <p className="text-xs text-slate-400 mt-1">Cliquez sur "Nouvelle facture" pour en ajouter une</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 font-medium text-slate-600 text-xs">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600 text-xs">Type</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600 text-xs">Fournisseur</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600 text-xs">Description</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600 text-xs">HT</th>
                  <th className="text-right px-4 py-3 font-medium text-emerald-700 text-xs bg-emerald-50/50">TVA</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600 text-xs">TTC</th>
                  <th className="text-center px-4 py-3 font-medium text-slate-600 text-xs">Paiement</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600 text-xs w-20">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors" data-testid={`invoice-row-${inv.id}`}>
                    <td className="px-4 py-2.5 text-slate-700 whitespace-nowrap text-xs">{formatDate(inv.date)}</td>
                    <td className="px-4 py-2.5">
                      <span className="text-xs text-slate-600">{types[inv.type] || inv.type}</span>
                    </td>
                    <td className="px-4 py-2.5 font-medium text-slate-900 max-w-[140px] truncate text-xs">{inv.supplier_name || "-"}</td>
                    <td className="px-4 py-2.5 text-slate-500 max-w-[180px] truncate text-xs">{inv.description || inv.invoice_number || "-"}</td>
                    <td className="px-4 py-2.5 text-right text-slate-700 tabular-nums text-xs">{formatEur(inv.total_ht)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-emerald-700 bg-emerald-50/30 text-xs">{formatEur(inv.tva_amount)}</td>
                    <td className="px-4 py-2.5 text-right text-slate-900 font-medium tabular-nums text-xs">{formatEur(inv.total_ttc)}</td>
                    <td className="px-4 py-2.5 text-center">
                      {inv.paid_personally ? (
                        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200 font-medium" title={`Réglée en perso le ${inv.paid_at || '?'} — apport au compte courant associé`}>
                          <Wallet size={10} /> Perso
                        </span>
                      ) : (
                        <span className="text-[11px] text-slate-500">{PAYMENT_LABELS[inv.payment_method] || "-"}</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex gap-0.5 justify-end">
                        {inv.paid_personally ? (
                          <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-amber-50"
                            onClick={() => handleUnmarkPaidPersonally(inv)}
                            title="Annuler le règlement en perso"
                            data-testid={`unmark-perso-${inv.id}`}>
                            <X size={13} className="text-amber-500" />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-violet-50"
                            onClick={() => setPersoDialog(inv)}
                            title="Régler cette facture en perso (compte courant associé)"
                            data-testid={`mark-perso-${inv.id}`}>
                            <Wallet size={13} className="text-violet-500" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditInvoice(inv); setDialogOpen(true); }} data-testid={`edit-invoice-${inv.id}`}>
                          <Edit size={13} className="text-slate-400" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(inv.id)} data-testid={`delete-invoice-${inv.id}`}>
                          <Trash2 size={13} className="text-red-400" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 border-t-2 border-slate-200 font-semibold">
                  <td colSpan={4} className="px-4 py-3 text-xs text-slate-600">Total {periodLabel} ({invoices.length} facture{invoices.length > 1 ? "s" : ""})</td>
                  <td className="px-4 py-3 text-right text-xs text-slate-800 tabular-nums">{formatEur(totalHT)}</td>
                  <td className="px-4 py-3 text-right text-sm text-emerald-700 tabular-nums bg-emerald-50/50">{formatEur(totalTVA)}</td>
                  <td className="px-4 py-3 text-right text-xs text-slate-800 tabular-nums">{formatEur(totalTTC)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      {/* Dialog */}
      <SupplierInvoiceDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditInvoice(null); }}
        onCreated={handleCreated}
        editData={editInvoice}
        types={types}
      />

      {/* Régler en perso dialog */}
      <Dialog open={!!persoDialog} onOpenChange={(v) => { if (!v) setPersoDialog(null); }}>
        <DialogContent className="max-w-md" data-testid="perso-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2">
              <Wallet size={15} className="text-violet-600" /> Régler en perso
            </DialogTitle>
          </DialogHeader>
          {persoDialog && (
            <div className="space-y-3">
              <div className="p-2 bg-violet-50 rounded text-[11px]">
                <p className="font-medium text-slate-700">{persoDialog.supplier_name}</p>
                <p className="text-slate-500">{persoDialog.invoice_number || "—"} · {formatEur(persoDialog.total_ttc)}</p>
              </div>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                Cette facture sera comptée comme un <span className="font-semibold text-violet-700">apport au compte courant associé</span> :
                la société vous doit ce montant et pourra vous rembourser plus tard par virement.
              </p>
              <div>
                <Label className="text-xs">Date de paiement</Label>
                <DateField value={persoForm.paid_at}
                  onChange={e => setPersoForm({...persoForm, paid_at: e.target.value})}
                  className="h-8 text-xs" data-testid="perso-date" />
              </div>
              <div>
                <Label className="text-xs">Note (facultatif)</Label>
                <Input value={persoForm.note}
                  onChange={e => setPersoForm({...persoForm, note: e.target.value})}
                  placeholder="Ex. CB perso, espèces, virement perso..."
                  className="h-8 text-xs" data-testid="perso-note" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setPersoDialog(null)} className="text-xs h-8">Annuler</Button>
            <Button size="sm" onClick={handleMarkPaidPersonally} disabled={persoSaving}
              className="bg-violet-600 hover:bg-violet-700 text-white text-xs h-8" data-testid="perso-confirm">
              {persoSaving ? "Enregistrement..." : "Valider le paiement perso"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
